import { forwardRef } from "react";
import { formatMonto } from "../../utils/rentalCalculations";
import { nombreCiclo } from "../../utils/historialAjustes";

const MESES_FULL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const FREQ_LABELS = {
  TRIMESTRAL: "3 meses",
  CUATRIMESTRAL: "4 meses",
  SEMESTRAL: "6 meses",
  ANUAL: "12 meses",
};

// Ley y organismo emisor que corresponde a cada índice de actualización. Se
// muestra en el encabezado del comprobante. Editá estos valores si cambia la
// normativa o querés otra redacción.
const NORMA_POR_INDICE = {
  IPC: { ley: "Ley 27.737", organismo: "INDEC" },
  ICL: { ley: "Ley 27.551", organismo: "BCRA" },
  INDEX: { ley: "Ley 27.737", organismo: "BCRA" },
  CASA_PROPIA: { ley: "Ley 27.737", organismo: "BCRA" },
};

const fmtFechaCorta = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y.slice(2)}`;
};

const mesNombre = (iso) => {
  if (!iso) return "-";
  const [y, m] = iso.split("-");
  if (!y || !m) return "-";
  const mesIdx = Number(m) - 1;
  if (mesIdx < 0 || mesIdx > 11) return "-";
  return `${MESES_FULL[mesIdx]} ${y}`;
};

// Returns the month/year *one month before* the given ISO date. Used to label
// the card with "Abril" as the last month with the old monto when the new
// monto starts in "Mayo".
const mesAnteriorNombre = (iso) => {
  if (!iso) return "-";
  const [y, m] = iso.split("-");
  if (!y || !m) return "-";
  let mesIdx = Number(m) - 2; // -1 for zero-based, -1 for previous month
  let anio = Number(y);
  if (mesIdx < 0) {
    mesIdx += 12;
    anio -= 1;
  }
  if (mesIdx < 0 || mesIdx > 11) return "-";
  return `${MESES_FULL[mesIdx]} ${anio}`;
};

// Card que se va a convertir en imagen para WhatsApp. Estilo siempre claro,
// no respeta dark mode -- queremos que la imagen exportada sea consistente.
// Ancho fijo (440px) tipico de mensaje en WhatsApp.
const AjusteCard = forwardRef(function AjusteCard({ data }, ref) {
  if (!data) return null;
  const {
    contrato,
    montoAnterior,
    montoNuevo,
    porcentaje,
    desdeStr,
    hastaStr,
    fechaAumento,
    proximoAumento,
    tipoIndice,
    frecuencia,
    fuente,
    parcial,
    mesesInfo,
    historial,
  } = data;

  const moneda = contrato.moneda || "ARS";
  const norma = NORMA_POR_INDICE[tipoIndice];
  const leyTexto = norma ? `${norma.ley} emitida por el ${norma.organismo}` : "";
  const diferencia = Number(montoNuevo) - Number(montoAnterior);
  // El nuevo monto entra en vigencia en `fechaAumento`. El último mes con el
  // monto anterior es el mes previo a esa fecha.
  const mesNuevoISO = fechaAumento;
  const proximaFechaISO = proximoAumento;

  return (
    <div
      ref={ref}
      style={{ width: 440 }}
      className="bg-white text-gray-800 font-sans"
    >
      {/* Header */}
      <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white">
        <p className="text-xs uppercase tracking-wider opacity-90">Inmobiliaria Plorutti</p>
        <h2 className="text-xl font-bold mt-0.5">Ajuste de alquiler</h2>
        {leyTexto && (
          <p className="text-xs font-medium opacity-95 mt-0.5">
            {leyTexto}
          </p>
        )}
        <p className="text-sm opacity-90 mt-1">
          {contrato.locatarioNombre}
        </p>
        <p className="text-xs opacity-80">{contrato.propiedad}</p>
      </div>

      <div className="px-6 py-5 space-y-4">
        {/* Monto anterior (último mes con el valor viejo) */}
        <div className="bg-orange-50 border border-orange-100 rounded-2xl py-4 px-5 text-center">
          <p className="text-xs text-orange-600 font-medium uppercase tracking-wide">
            {fechaAumento ? `Hasta ${mesAnteriorNombre(fechaAumento)}` : "Anterior"}
          </p>
          <p className="text-3xl font-bold text-gray-700 mt-1">
            {formatMonto(montoAnterior, moneda)}
          </p>
        </div>

        {/* Monto nuevo - destacado */}
        <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl py-5 px-5 text-center relative">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-400 text-white text-xs font-bold px-4 py-1 rounded-full">
            DESDE
          </span>
          <p className="text-sm text-gray-600 font-medium uppercase tracking-wide mt-1">
            {mesNuevoISO ? mesNombre(mesNuevoISO) : "-"}
          </p>
          <p className="text-4xl font-bold text-gray-800 mt-2">
            {formatMonto(montoNuevo, moneda)}
          </p>
          <p className="text-sm font-semibold text-emerald-600 mt-1">
            +{formatMonto(diferencia, moneda)} ({Number(porcentaje).toFixed(2)}%)
          </p>
        </div>

        {/* Detalles */}
        <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Indice</span>
            <span className="font-medium text-gray-700">{tipoIndice || "-"}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Frecuencia</span>
            <span className="font-medium text-gray-700">{FREQ_LABELS[frecuencia] || frecuencia || "-"}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Periodo del calculo</span>
            <span className="font-medium text-gray-700">
              {desdeStr || "-"} a {hastaStr || "-"}
            </span>
          </div>
          {fuente && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Fuente</span>
              <span className="font-medium text-gray-700">{fuente}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-200">
            <span className="text-gray-500">Proximo aumento</span>
            <span className="font-medium text-gray-700">{fmtFechaCorta(proximaFechaISO)}</span>
          </div>
        </div>

        {parcial && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            <span className="font-semibold">Aproximado: </span>
            ajuste calculado con {mesesInfo} meses de indices disponibles. El valor final
            puede variar al publicarse el resto del periodo.
          </div>
        )}

        {Array.isArray(historial) && historial.length > 1 && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-600 tracking-wide">
                Histórico de ajustes
              </p>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-white">
                  <th className="text-left px-3 py-1.5 font-medium text-gray-500">Ciclo</th>
                  <th className="text-left px-3 py-1.5 font-medium text-gray-500">Fecha</th>
                  <th className="text-right px-3 py-1.5 font-medium text-gray-500">Aumento</th>
                  <th className="text-right px-3 py-1.5 font-medium text-gray-500">Valor</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((c, i) => {
                  const ultimo = i === historial.length - 1;
                  return (
                    <tr
                      key={i}
                      className={`border-b border-gray-50 last:border-b-0 ${
                        ultimo ? "bg-emerald-50" : ""
                      }`}
                    >
                      <td className={`px-3 py-1.5 ${ultimo ? "font-semibold text-emerald-700" : "text-gray-600"}`}>
                        {nombreCiclo(c.numero, frecuencia)}
                      </td>
                      <td className={`px-3 py-1.5 ${ultimo ? "text-emerald-700 font-medium" : "text-gray-600"}`}>
                        {fmtFechaCorta(c.fecha)}
                      </td>
                      <td className={`px-3 py-1.5 text-right ${ultimo ? "text-emerald-700 font-semibold" : "text-gray-700"}`}>
                        {c.porcentaje != null ? (
                          <>
                            {c.porcentaje}%
                            {c.esEstimado && <span className="text-amber-500">*</span>}
                          </>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className={`px-3 py-1.5 text-right ${ultimo ? "text-emerald-700 font-semibold" : "text-gray-700 font-medium"}`}>
                        {c.valor != null ? formatMonto(c.valor, contrato.moneda) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {historial.some((c) => c.esEstimado) && (
              <p className="text-[9px] text-gray-400 px-3 py-1.5 italic border-t border-gray-100">
                * Estimado a partir de IPC. El último ciclo es el aumento aplicado.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
        <p className="text-[10px] text-gray-400">
          Generado el {new Date().toLocaleDateString("es-AR")} · Inmobiliaria Plorutti
        </p>
      </div>
    </div>
  );
});

export default AjusteCard;
